import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, Database, RefreshCw, Save, Search, ToggleLeft, ToggleRight, Trash2, Upload } from 'lucide-react';
import {
  apiService,
  agentApiService,
  AiAgentKbDocument,
  AiAgentSettings,
  Campaign,
  ChannelType,
} from '../../services/api';

const DEFAULT_SETTINGS: AiAgentSettings = {
  key: 'default',
  aiEnabled: true,
  channelInstructions: {
    whatsapp: {
      systemInstruction: '',
      followUpInstruction: '',
      nonReplyLevel: 0,
    },
    facebook_messenger: {
      systemInstruction: '',
      followUpInstruction: '',
      nonReplyLevel: 0,
    },
    instagram: {
      systemInstruction: '',
      followUpInstruction: '',
      nonReplyLevel: 0,
    },
  },
  modelProvider: 'gemini',
  modelTier: 'cheap',
  kbEnabled: false,
  kbTopK: 5,
};

const CHANNEL_OPTIONS: Array<{ value: ChannelType; label: string }> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'facebook_messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
];

const DEFAULT_CHANNELS: ChannelType[] = CHANNEL_OPTIONS.map(({ value }) => value);

const normalizeChannels = (channels?: ChannelType[]): ChannelType[] => {
  const valid = (channels || []).filter((channel) =>
    CHANNEL_OPTIONS.some((option) => option.value === channel),
  );
  if (valid.length === 0) return [...DEFAULT_CHANNELS];
  return Array.from(new Set(valid));
};

export const AgentKnowledgeBasePage: React.FC = () => {
  const [settings, setSettings] = useState<AiAgentSettings>(DEFAULT_SETTINGS);
  const [kbDocs, setKbDocs] = useState<AiAgentKbDocument[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedUploadChannels, setSelectedUploadChannels] = useState<ChannelType[]>([
    ...DEFAULT_CHANNELS,
  ]);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const campaignDropdownRef = useRef<HTMLDivElement | null>(null);

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign._id === selectedCampaignId) || null,
    [campaigns, selectedCampaignId],
  );

  const filteredCampaigns = useMemo(() => {
    const normalizedQuery = campaignSearch.trim().toLowerCase();
    if (!normalizedQuery) return campaigns;
    return campaigns.filter((campaign) => campaign.name.toLowerCase().includes(normalizedQuery));
  }, [campaignSearch, campaigns]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, docsRes, campaignsRes] = await Promise.all([
        agentApiService.getSettings(),
        agentApiService.getKbDocuments(),
        apiService.getCampaigns(),
      ]);
      setSettings({ ...DEFAULT_SETTINGS, ...settingsRes });
      setKbDocs(docsRes);
      setCampaigns(campaignsRes);
    } catch (error) {
      console.error('Failed to load knowledge base data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCampaignDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!selectedCampaignId) {
      setSelectedUploadChannels([...DEFAULT_CHANNELS]);
      return;
    }
    setSelectedUploadChannels(normalizeChannels(selectedCampaign?.channels));
  }, [selectedCampaignId, selectedCampaign]);

  const handleSettingsChange = (key: keyof AiAgentSettings, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const toggleUploadChannel = (channel: ChannelType) => {
    setSelectedUploadChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((item) => item !== channel)
        : [...prev, channel],
    );
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updated = await agentApiService.updateSettings({
        kbEnabled: settings.kbEnabled,
        kbTopK: settings.kbTopK,
      });
      setSettings({ ...DEFAULT_SETTINGS, ...updated });
    } catch (error) {
      console.error('Failed to save KB settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedCampaignId) {
      alert('Please select a campaign before uploading a document.');
      event.target.value = '';
      return;
    }
    if (selectedUploadChannels.length === 0) {
      alert('Please select at least one channel for this document.');
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      await agentApiService.uploadKbPdf(
        file,
        selectedCampaignId,
        selectedUploadChannels,
      );
      await loadAll();
    } catch (error) {
      console.error('Failed to upload PDF:', error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleResetKnowledgeBase = async () => {
    const confirmed = window.confirm(
      'Reset Knowledge Base?\n\nThis will clear all indexed vectors and mark all documents as needing re-upload.',
    );
    if (!confirmed) return;

    setResetting(true);
    try {
      const result = await agentApiService.resetKnowledgeBase();
      await loadAll();
      alert(
        `Knowledge base reset completed.\nCollection deleted: ${result.collectionDeleted ? 'Yes' : 'No'}\nDocuments marked for re-upload: ${result.docsMarkedForReupload}`,
      );
    } catch (error) {
      console.error('Failed to reset knowledge base:', error);
      alert('Failed to reset knowledge base. Check server logs.');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteDocument = async (doc: AiAgentKbDocument) => {
    const displayName = doc.originalName || doc.filename;
    const confirmed = window.confirm(`Delete "${displayName}" from knowledge base?`);
    if (!confirmed) return;

    setDeletingDocId(doc._id);
    try {
      await agentApiService.deleteKbDocument(doc._id);
      await loadAll();
    } catch (error) {
      console.error('Failed to delete knowledge base document:', error);
      alert('Failed to delete document. Check server logs.');
    } finally {
      setDeletingDocId(null);
    }
  };

  return (
    <div className="page-section">
      <header className="page-heading">
        <div>
          <p className="eyebrow">AI Sales Agent</p>
          <h2>Knowledge base</h2>
        </div>
        <div className="heading-actions">
          <button className="ghost-button" onClick={loadAll} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button
            className="ghost-button"
            onClick={handleResetKnowledgeBase}
            disabled={resetting}
            style={{ borderColor: 'var(--error)', color: 'var(--error)', display: 'none' }}
          >
            <AlertTriangle size={16} />
            {resetting ? 'Resetting...' : 'Reset KB'}
          </button>
          <button className="primary-button" onClick={saveSettings} disabled={saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save KB Settings'}
          </button>
        </div>
      </header>

      <section className="panel">
        <header className="panel-header">
          <div>
            <p className="eyebrow">
              <Database size={14} /> Campaign-scoped PDF ingestion
            </p>
            <h3>Knowledge base settings</h3>
          </div>
        </header>

        <div
          className="settings-grid"
          style={{ marginTop: '1rem', gridTemplateColumns: '1fr auto', alignItems: 'end' }}
        >
          <div className="form-group" ref={campaignDropdownRef} style={{ position: 'relative' }}>
            <label>Campaign for uploaded document</label>
            <div
              onClick={() => setShowCampaignDropdown((prev) => !prev)}
              className="input-field"
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '44px',
              }}
            >
              <span style={{ color: selectedCampaign ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {selectedCampaign ? selectedCampaign.name : 'Search and select campaign...'}
              </span>
              <ChevronDown
                size={16}
                style={{ transform: showCampaignDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}
              />
            </div>

            {showCampaignDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '0.5rem',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                  zIndex: 20,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '0.5rem 0.65rem',
                      background: 'var(--surface-muted)',
                    }}
                  >
                    <Search size={15} style={{ color: 'var(--text-secondary)' }} />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Search campaign..."
                      value={campaignSearch}
                      onChange={(event) => setCampaignSearch(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        minHeight: 'auto',
                      }}
                      autoFocus
                    />
                  </div>
                </div>

                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  {filteredCampaigns.length === 0 && (
                    <div
                      style={{
                        padding: '0.75rem',
                        fontSize: '0.9rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      No campaigns found.
                    </div>
                  )}
                  {filteredCampaigns.map((campaign) => (
                    <button
                      key={campaign._id}
                      type="button"
                      onClick={() => {
                        setSelectedCampaignId(campaign._id);
                        setShowCampaignDropdown(false);
                        setCampaignSearch('');
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        borderBottom: '1px solid var(--border)',
                        background:
                          selectedCampaignId === campaign._id ? 'var(--surface-muted)' : 'var(--surface)',
                        color: 'var(--text-primary)',
                        padding: '0.75rem',
                        cursor: 'pointer',
                      }}
                    >
                      {campaign.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <label className="ghost-button" style={{ alignSelf: 'end' }}>
            <Upload size={16} />
            {uploading ? 'Uploading...' : 'Upload PDF'}
            <input type="file" accept="application/pdf" onChange={handleUpload} hidden />
          </label>
        </div>

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>Channels for uploaded document</label>
          <div style={{ display: 'flex', gap: '0.55rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            {CHANNEL_OPTIONS.map((option) => {
              const checked = selectedUploadChannels.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleUploadChannel(option.value)}
                  style={{
                    border: checked ? '1px solid var(--accent)' : '1px solid var(--border)',
                    background: checked ? 'rgba(37, 99, 235, 0.12)' : 'var(--surface)',
                    color: checked ? 'var(--accent)' : 'var(--text-primary)',
                    borderRadius: '999px',
                    padding: '0.38rem 0.8rem',
                    cursor: 'pointer',
                    fontSize: '0.84rem',
                    fontWeight: 500,
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <small style={{ color: 'var(--text-secondary)' }}>
            Only selected channels can retrieve snippets from this document.
          </small>
        </div>

        <div className="settings-grid" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label>KB Enabled</label>
            <button
              className="ghost-button"
              onClick={() => handleSettingsChange('kbEnabled', !settings.kbEnabled)}
            >
              {settings.kbEnabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
              {settings.kbEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
          <div className="form-group">
            {/* <label>Top K</label>
            <input
              className="input-field"
              type="number"
              min={1}
              value={settings.kbTopK || 4}
              onChange={(e) => handleSettingsChange('kbTopK', Number(e.target.value))}
            /> */}
          </div>
        </div>

        <div className="panel-footer">
          <span>{kbDocs.length} document(s) in knowledge base</span>
        </div>

        <div className="table table-kb" style={{ marginTop: '1rem' }}>
          <div className="table-head" style={{ gridTemplateColumns: '2fr 1.1fr 1.1fr 0.9fr 0.9fr 1.2fr 0.7fr' }}>
            <span>Document</span>
            <span>Campaign</span>
            <span>Channels</span>
            <span>Status</span>
            <span>Chunks</span>
            <span>Uploaded</span>
            <span>Actions</span>
          </div>
          <div className="table-body">
            {kbDocs.length === 0 && (
              <div className="empty-state">No knowledge base documents yet.</div>
            )}
            {kbDocs.map((doc) => (
              <div
                key={doc._id}
                className="table-row"
                style={{ gridTemplateColumns: '2fr 1.1fr 1.1fr 0.9fr 0.9fr 1.2fr 0.7fr' }}
              >
                <span>
                  <strong>{doc.originalName || doc.filename}</strong>
                  <small>{doc.filename}</small>
                </span>
                <span>{doc.campaignName || 'Unassigned'}</span>
                <span className="tags">
                  {normalizeChannels(doc.channels).map((channel) => (
                    <span key={`${doc._id}-${channel}`} className="pill muted">
                      {channel === 'facebook_messenger'
                        ? 'Messenger'
                        : channel === 'whatsapp'
                          ? 'WhatsApp'
                          : 'Instagram'}
                    </span>
                  ))}
                </span>
                <span>
                  <span className={`pill ${doc.status === 'ready' ? 'success' : 'muted'}`}>
                    {doc.status}
                  </span>
                </span>
                <span>{doc.chunks}</span>
                <span>{new Date(doc.createdAt).toLocaleString()}</span>
                <span>
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => handleDeleteDocument(doc)}
                    disabled={deletingDocId === doc._id}
                    title="Delete document"
                    style={{ padding: '0.4rem 0.55rem' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
